import React, { useEffect } from 'react';
import { NavLink, useHistory } from 'react-router-dom';
// Chakra imports
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useColorModeValue,
  useTheme,
} from '@chakra-ui/react';

import DefaultLayout from './Layout';

import { FcGoogle } from 'react-icons/fc';
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { RiEyeCloseLine } from 'react-icons/ri';

import { useAuth } from '../../Context/AuthContext';

function Login() {
  // Chakra color mode
  const textColor = useColorModeValue('#1B254B', 'white');
  const textColorSecondary = '#A0AEC0';
  const textColorDetails = useColorModeValue('#1B254B', '#A3AED0');
  const textColorBrand = useColorModeValue('#422AFB', 'white');
  const brandStars = useColorModeValue('#422AFB', '#7551FF');
  const googleBg = useColorModeValue('#F4F7FE', 'rgba(255, 255, 255, 0.08)');
  const googleText = useColorModeValue('#1B254B', 'white');
  const googleHover = useColorModeValue(
    { bg: '#E2E8F0' },
    { bg: 'rgba(255, 255, 255, 0.16)' }
  );
  const googleActive = useColorModeValue(
    { bg: '#F4F7FE' },
    { bg: 'rgba(255, 255, 255, 0.08)' }
  );
  const [show, setShow] = React.useState(false);
  const [error, setError] = React.useState('');
  const emailRef = React.useRef();
  const passwordRef = React.useRef();

  const { login, signInWithGoogle, currentUser } = useAuth();
  const history = useHistory();

  const handleClick = () => setShow(!show);

  async function handleGoogleLoginClick(e) {
    e.preventDefault();

    try {
      setError('');
      e.target.disabled = true;

      const result = await signInWithGoogle();
      // The signed-in user info.

      const user = result.user;
      if (user) {
        const token = await user.getIdToken();
        localStorage.setItem('accessToken', token);
        history.push('/');
      }
    } catch {
      setError('Failed to log in: ' + String(error?.code)?.split('/')[1]);
      setTimeout(() => {
        setError('');
        e.target.disabled = false;
      }, 3000);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      setError('');
      e.target.disabled = true;
      // setLoading(true);
      const result = await login(
        emailRef.current.value,
        passwordRef.current.value
      );

      const user = result.user;
      if (user) {
        const token = await user.getIdToken();
        localStorage.setItem('accessToken', token);
        history.push('/');
      }
    } catch (error) {
      setError('Failed to log in: ' + String(error?.code)?.split('/')[1]);

      setTimeout(() => {
        setError('');
        e.target.disabled = false;
      }, 3000);
    }
  }

  useEffect(() => {
    if (currentUser) {
      history.push('/');
    }
  }, []);

  return (
    <DefaultLayout>
      <Box w="100%">
        {error && (
          <Alert status="error">
            <AlertIcon />
            {error}
          </Alert>
        )}
      </Box>
      <Flex
        maxW={{ base: '100%', md: 'max-content' }}
        w="100%"
        mx={{ base: 'auto', lg: '0px' }}
        me="auto"
        h="100%"
        alignItems="start"
        justifyContent="center"
        mb={{ base: '30px', md: '60px' }}
        px={{ base: '25px', md: '0px' }}
        mt={{ base: '40px', md: '14vh' }}
        flexDirection="column"
      >
        <Box me="auto">
          <Heading color={textColor} fontSize="36px" mb="10px">
            Sign In
          </Heading>
          <Text
            mb="36px"
            ms="4px"
            color={textColorSecondary}
            fontWeight="400"
            fontSize="md"
          >
            Enter your email and password to sign in!
          </Text>
        </Box>
        <Flex
          zIndex="2"
          direction="column"
          w={{ base: '100%', md: '420px' }}
          maxW="100%"
          background="transparent"
          borderRadius="15px"
          mx={{ base: 'auto', lg: 'unset' }}
          me="auto"
          mb={{ base: '20px', md: 'auto' }}
        >
          <Button
            fontSize="sm"
            me="0px"
            mb="26px"
            py="15px"
            h="50px"
            borderRadius="16px"
            bg={googleBg}
            color={googleText}
            fontWeight="500"
            _hover={googleHover}
            _active={googleActive}
            _focus={googleActive}
            onClick={handleGoogleLoginClick}
          >
            <Icon as={FcGoogle} w="20px" h="20px" me="10px" />
            Sign in with Google
          </Button>
          <Flex align="center" mb="25px">
            <Flex h="1px" w="100%" bg="rgba(135, 140, 189, 0.3)"></Flex>
            <Text color="#A0AEC0" mx="14px">
              or
            </Text>
            <Flex h="1px" w="100%" bg="rgba(135, 140, 189, 0.3)"></Flex>
          </Flex>
          <FormControl>
            <FormLabel
              display="flex"
              ms="4px"
              fontSize="sm"
              fontWeight="500"
              color={textColor}
              mb="8px"
            >
              Email<Text color={brandStars}>*</Text>
            </FormLabel>
            <Input
              isRequired={true}
              variant="auth"
              fontSize="sm"
              ms={{ base: '0px', md: '0px' }}
              type="email"
              placeholder="mail@gmail.com"
              mb="24px"
              fontWeight="500"
              size="lg"
              outline="1px solid #e0e5f2"
              ref={emailRef}
            />
            <FormLabel
              ms="4px"
              fontSize="sm"
              fontWeight="500"
              color={textColor}
              display="flex"
            >
              Password<Text color={brandStars}>*</Text>
            </FormLabel>
            <InputGroup size="md">
              <Input
                isRequired={true}
                fontSize="sm"
                placeholder="Min. 8 characters"
                mb="24px"
                size="lg"
                type={show ? 'text' : 'password'}
                variant="auth"
                outline="1px solid #e0e5f2"
                ref={passwordRef}
              />
              <InputRightElement display="flex" alignItems="center" mt="4px">
                <Icon
                  color={textColorSecondary}
                  _hover={{ cursor: 'pointer' }}
                  as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                  onClick={handleClick}
                />
              </InputRightElement>
            </InputGroup>
            <Flex justifyContent="space-between" align="center" mb="24px">
              <NavLink to="/register?reset=true">
                <Text
                  color={textColorBrand}
                  fontSize="sm"
                  w="124px"
                  fontWeight="500"
                >
                  Forgot password?
                </Text>
              </NavLink>
            </Flex>
            <Button
              fontSize="sm"
              variant="brand"
              fontWeight="500"
              w="100%"
              h="50"
              mb="24px"
              background="#422AFB"
              color="white"
              onClick={handleSubmit}
            >
              Sign In
            </Button>
          </FormControl>
          <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="start"
            maxW="100%"
            mt="0px"
          >
            <Text color={textColorDetails} fontWeight="400" fontSize="14px">
              Not registered yet?
              <NavLink to="/register">
                <Text
                  color={textColorBrand}
                  as="span"
                  ms="5px"
                  fontWeight="500"
                >
                  Create an Account
                </Text>
              </NavLink>
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </DefaultLayout>
  );
}

export default Login;
